import { ReactElement, useEffect, useMemo, useState } from 'react'
import { AxiosResponse } from 'axios'
import { useNavigate } from 'react-router-dom'
import NaloxoneSurvey, { NaloxoneSurveyApi } from '../components/survey/NaloxoneSurvey'
import apiServices from '../libs/services'
import { LoadingSpinner } from '../components/atoms'
import { useAuth } from '../hooks/useAuth'

export const NALOXONE_SURVEY_ID_SLUG = 'NALOXONE_SURVEY'

// Signed-in path. getSurvey() creates the response; start() just returns the id created there.
const buildAuthenticatedNaloxoneApi = (surveyId: string): NaloxoneSurveyApi => {
  let cachedResponseId: string | null = null

  return {
    getSurvey: async (): Promise<AxiosResponse> => {
      const startRes = await apiServices.startSurvey(surveyId)
      const responseId = startRes?.data?.surveyResponseId as string | undefined
      if (startRes == null || responseId == null) {
        return startRes as AxiosResponse
      }
      cachedResponseId = responseId
      return await apiServices.getSurveyWithProgress(surveyId, responseId)
    },
    start: async (): Promise<AxiosResponse> => ({ data: { surveyResponseId: cachedResponseId } } as AxiosResponse),
    submitAnswer: async (responseId, questionId, data) =>
      await apiServices.submitSurveyAnswer(surveyId, responseId, questionId, data),
    setAnonTag: async (responseId, anonTag) =>
      await apiServices.setSurveyAnonTag(surveyId, responseId, anonTag),
    complete: async (responseId) =>
      await apiServices.completeSurvey(surveyId, responseId)
  }
}

interface NaloxoneSurveyPageProps {
  authenticated?: boolean
}

function NaloxoneSurveyPage ({ authenticated = false }: NaloxoneSurveyPageProps): ReactElement {
  const navigate = useNavigate()
  const { refreshUserProfile } = useAuth()
  const [surveyId, setSurveyId] = useState<string | null>(null)
  const [loading, setLoading] = useState(authenticated)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!authenticated) return

    const fetchSurveyId = async (): Promise<void> => {
      try {
        const res = await apiServices.getSurveyByUserGeneratedId(NALOXONE_SURVEY_ID_SLUG)
        const id = res.data?.id as string | undefined
        if (id === undefined || id === null) {
          setError('Survey ID not found in response.')
          return
        }
        setSurveyId(id)
      } catch (err: any) {
        console.error('Failed to fetch survey metadata.', err)
        setError('Failed to load survey metadata.')
      } finally {
        setLoading(false)
      }
    }

    void fetchSurveyId()
  }, [authenticated])

  const handleFinish = async (): Promise<void> => {
    if (!authenticated) return
    await refreshUserProfile()
    navigate('/user-dashboard', { replace: true })
  }

  // Memoized so a parent re-render mid-flow doesn't hand NaloxoneSurvey a new adapter identity.
  const api = useMemo(
    () => (authenticated && surveyId !== null ? buildAuthenticatedNaloxoneApi(surveyId) : undefined),
    [authenticated, surveyId]
  )

  if (authenticated && loading) {
    return (
      <div className='bg-dopage-black min-h-screen flex items-center justify-center'>
        <LoadingSpinner className='w-[25vmin] min-w-[80px] max-w-[240px]' />
      </div>
    )
  }

  if (authenticated && (error !== null || surveyId === null)) {
    return (
      <div className='bg-dopage-black min-h-screen flex items-center justify-center text-red-500 font-primary'>
        {error ?? 'An unexpected error occurred.'}
      </div>
    )
  }

  return <NaloxoneSurvey api={api} onFinish={authenticated ? () => { void handleFinish() } : undefined} />
}

export default NaloxoneSurveyPage
